import { unknownErrorToError } from '@w/utils'
import type { Change } from '@w/content-blocks-editor/doc-bindings-for-page'

/** A burst commits `idleMs` after its last edit, but never sooner than `minGapMs` after the previous commit started
 * (a throttle, as Liveblocks' `throttle` on outgoing updates), and at the latest `maxWaitMs` after its first edit. */
export type PageCommitPolicy = { idleMs: number; maxWaitMs: number; minGapMs?: number }
export const defaultPageCommitPolicy: PageCommitPolicy = { idleMs: 150, minGapMs: 1_000, maxWaitMs: 2_000 }

/** Buffer authored changes until persistence; the editor already owns the live Doc. */
export function createPageCommitBuffer<BufferedEdit = Change>({ policy, commit, onError, requestFlush, localCommitBoundary = false }: {
  policy: PageCommitPolicy
  localCommitBoundary?: boolean
  commit: (changes: BufferedEdit[], run?: WriteRun, locallyCommitted?: () => void) => Promise<void>
  onError: (error: unknown) => void
  requestFlush?: () => Promise<void>
}) {
  let queued: BufferedEdit[] = []
  let active: BufferedEdit[] = []
  type Waiter = (result: 'committed' | 'discarded') => void
  let queuedWaiters: Waiter[] = []
  let activeWaiters: Waiter[] = []
  let idle: ReturnType<typeof setTimeout> | undefined
  let deadline: ReturnType<typeof setTimeout> | undefined
  let running: Promise<void> | undefined
  const acknowledgements = new Set<Promise<void>>()
  let failed: { error: Error } | undefined
  let due = false
  let revoked = false
  let flushAll = false
  let lastStart = -Infinity

  /** The idle wait: `idleMs`, stretched to keep commits `minGapMs` apart. */
  function idleWait() {
    return Math.max(policy.idleMs, lastStart + (policy.minGapMs ?? 0) - Date.now())
  }

  function clearTimers() {
    clearTimeout(idle)
    clearTimeout(deadline)
    idle = undefined
    deadline = undefined
  }

  function settleBurst(waiters: Waiter[]) {
    active = []
    activeWaiters = []
    for (const resolve of waiters) resolve(revoked ? 'discarded' : 'committed')
  }

  function observeAcknowledgement(acknowledgement: Promise<void>, persisted: () => boolean) {
    acknowledgements.add(acknowledgement)
    void acknowledgement.then(() => { acknowledgements.delete(acknowledgement); return null }, error => {
      acknowledgements.delete(acknowledgement)
      if (persisted()) {
        failed = { error: unknownErrorToError(error) }
        clearTimers()
        onError(error)
      }
    })
  }

  async function drain(run?: WriteRun) {
    while (queued.length > 0) {
      if (!due && !flushAll) break
      due = false
      clearTimers()
      const burst = queued
      const waiters = queuedWaiters
      active = burst
      activeWaiters = waiters
      queued = []
      queuedWaiters = []
      lastStart = Date.now()
      let persisted = false
      const local = Promise.withResolvers<void>()
      const locallyCommitted = () => {
        if (persisted) return
        persisted = true
        settleBurst(waiters)
        local.resolve()
      }
      try {
        const acknowledgement = localCommitBoundary ? commit(burst, run, locallyCommitted) : commit(burst, run)
        observeAcknowledgement(acknowledgement, () => persisted)
        if (localCommitBoundary) await Promise.race([local.promise, acknowledgement])
        else await acknowledgement
        locallyCommitted()
      }
      catch (error) {
        if (!persisted && !revoked) {
          queued = [...burst, ...queued]
          queuedWaiters = [...waiters, ...queuedWaiters]
          active = []
          activeWaiters = []
        }
        failed = { error: unknownErrorToError(error) }
        clearTimers()
        onError(error)
        throw error
      }
    }
  }

  function start(run?: WriteRun): Promise<void> {
    if (failed) return Promise.reject(failed.error)
    running ??= drain(run).finally(() => { running = undefined; flushAll = false })
    return running
  }

  /** `all` is a barrier (navigation, sign-out, pagehide): it commits everything queued, now. `due` is the idle timer's
   * request through the write gate: it commits only a burst whose timer fired, so an edit queued while that commit
   * runs keeps its own timer and the min gap. */
  async function flush(run?: WriteRun, mode: FlushMode = 'all'): Promise<void> {
    // Discard settled these edits. Late cleanup of a retired reader must
    // not fail the next route's save barrier.
    if (revoked) return
    if (mode === 'due') return flushDue(run)
    do {
      clearTimers()
      flushAll = true
      await start(run)
    } while (queued.length > 0)
    await Promise.all(acknowledgements)
    if (!revoked && failed) throw failed.error
  }

  async function flushDue(run?: WriteRun) {
    while (queued.length > 0) {
      if (!due) break
      await start(run)
    }
  }

  function scheduledFlush() {
    clearTimers()
    due = true
    // drain reports failures through the page's existing error boundary. Retain
    // failed changes and keep rejecting barriers so no reader passes a failed save.
    void (requestFlush ? requestFlush() : flushDue()).catch(() => {})
  }

  return {
    pending() { return [...active, ...queued] },
    push(next: BufferedEdit[]): Promise<'committed' | 'discarded'> {
      if (revoked) { onError(new DOMException('Space access revoked', 'AbortError')); return Promise.resolve('discarded') }
      if (next.length === 0) return Promise.resolve('committed')
      const completion = Promise.withResolvers<'committed' | 'discarded'>()
      queued = [...queued, ...next]
      queuedWaiters.push(completion.resolve)
      failed = undefined
      clearTimeout(idle)
      idle = setTimeout(scheduledFlush, idleWait())
      deadline ??= setTimeout(scheduledFlush, policy.maxWaitMs)
      return completion.promise
    },
    flush,
    fail(error: unknown) {
      failed = { error: unknownErrorToError(error) }
      clearTimers()
      onError(error)
    },
    stop: clearTimers,
    resume() {
      if (revoked || queued.length === 0) return
      failed = undefined
      clearTimers()
      idle = setTimeout(scheduledFlush, idleWait())
      deadline = setTimeout(scheduledFlush, policy.maxWaitMs)
    },
    discard() {
      revoked = true
      clearTimers()
      for (const resolve of [...activeWaiters, ...queuedWaiters]) resolve('discarded')
      active = []
      queued = []
      activeWaiters = []
      queuedWaiters = []
      failed = { error: new DOMException('Space access revoked', 'AbortError') }
    },
  }
}

export class SigningOutError extends Error {
  constructor() { super('Signing out'); this.name = 'SigningOutError' }
}
export type WriteRun = <T>(operation: () => PromiseLike<T>) => Promise<T>
export type FlushMode = 'all' | 'due'

/** One admission boundary for every authored workspace write. */
export function createWriteGate() {
  let phase: 'open' | 'draining' | 'closed' = 'open'
  const buffers = new Set<{ workspaceId: string; flush: (run?: WriteRun, mode?: FlushMode) => Promise<void>; revoke?: () => void; stop?: () => void; resume?: () => void }>()
  const inFlight = new Set<Promise<unknown>>()
  let signingOut: Promise<void> | undefined
  let drainFailure: { error: unknown } | undefined
  function checkDrainFailure() {
    if (drainFailure) throw drainFailure.error
  }
  function admit<T>(operation: () => PromiseLike<T>, internal = false): Promise<T> {
    if (phase === 'closed' || phase === 'draining' && !internal) return Promise.reject(new SigningOutError())
    const task = Promise.resolve().then(operation)
    inFlight.add(task)
    void task.then(() => inFlight.delete(task), error => {
      if (phase === 'draining') drainFailure ??= { error }
      inFlight.delete(task)
    })
    return task
  }
  function flush(workspaceId?: string, mode: FlushMode = 'all'): Promise<void> {
    // One owner supplies every buffer's capability, including timers and moves.
    if (phase === 'closed') return Promise.resolve()
    const task = (async () => {
      for (const entry of buffers) if (workspaceId === undefined || entry.workspaceId === workspaceId) {
        await entry.flush(operation => admit(operation, true), mode)
      }
    })()
    // Buffers serialize their own local commits. A reader acknowledgement may
    // need a later local write, so it must never block another flush's admission.
    return task
  }
  return {
    get phase() { return phase },
    run<T>(operation: () => PromiseLike<T>) { return admit(operation) },
    register(workspaceId: string, save: (run?: WriteRun, mode?: FlushMode) => Promise<void>, revoke?: () => void, stop?: () => void, resume?: () => void) {
      const entry = { workspaceId, flush: save, revoke, stop, resume }
      buffers.add(entry)
      if (phase !== 'open') stop?.()
      return () => { buffers.delete(entry) }
    },
    flush,
    revoke(workspaceId: string) {
      for (const entry of buffers) if (entry.workspaceId === workspaceId) {
        buffers.delete(entry); entry.revoke?.()
      }
    },
    signOut(logout: () => Promise<void>) {
      if (signingOut) return signingOut
      drainFailure = undefined
      phase = 'draining'
      for (const entry of buffers) entry.stop?.()
      signingOut = (async () => {
        try {
          while (true) {
            await flush()
            checkDrainFailure()
            if (inFlight.size === 0) break
            await Promise.all(inFlight)
          }
        } catch (error) {
          phase = 'open'
          for (const entry of buffers) entry.resume?.()
          throw error
        }
        phase = 'closed'
        await logout()
      })().finally(() => { signingOut = undefined })
      return signingOut
    },
    reopen() { phase = 'open' },
  }
}
